import { readXlsxMatrices, type XlsxMatrixSheet } from '@obraplan/table-reader/xlsx-matrix';
import { looksLikeZip } from '@obraplan/table-reader/xlsx-reader';
import type {
  AllocationBasis,
  ImportRowPreview,
  ImportRowStatus,
  PurchaseKind,
  PurchaseLineViewOrigin,
} from '@obraplan/core/application/contracts/purchase-lines';
import {
  detectPurchaseWorkbook,
  looksLikeLabor,
  looksLikePurchaseLines,
  readLaborTemplate,
  readPurchaseLinesTemplate,
  type LaborRowData,
  type Matrix,
  type MaterialTotalData,
  type PurchaseLineRowData,
  type RowIssue,
  type SkippedLine,
} from '@obraplan/core/domain/schedule/purchase-template/index';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';

/**
 * A planilha de itens de compra até as linhas que o servidor recebe (Fase 26, ADR-170).
 *
 * Como na Fase 25, a importação tem duas metades. A deste módulo roda **no navegador** e não
 * grava nada: abre o `.xlsx`, acha as abas "Itens de compra" e "Mão de obra" e as lê com os
 * leitores do domínio (`purchase-template/*`). A outra metade — conferir contra o cadastro e
 * gravar — é do servidor, que só recebe linhas normalizadas.
 *
 * Também mora aqui o que as telas de compra repetem: os rótulos de origem, base do rateio e
 * classe, a formatação da quantidade e a junção da prévia do servidor com as linhas enviadas.
 */

/** O servidor recusa mais linhas do que isto por chamada (`previewPurchaseLinesImportSchema`). */
export const MAX_PURCHASE_ROWS = 20000;

// ── Rótulos ────────────────────────────────────────────────────────────────────────

/** De onde veio a linha (RN-346; o quantitativo desde a Fase 35, RN-449). */
export const ORIGIN_LABEL: Readonly<Record<PurchaseLineViewOrigin, string>> = {
  MANUAL: 'Manual',
  PLANILHA: 'Planilha',
  ORCAMENTO: 'Orçamento',
  QUANTITATIVO: 'Quantitativo',
};

/** Como a quantidade chegou ao pavimento (RN-345), curto para a tabela. */
export const BASIS_LABEL: Readonly<Record<AllocationBasis, string>> = {
  DIRETO: 'Direto',
  RATEIO_OBRA: 'Rateio da obra',
  RATEIO_ANDARES: 'Rateio por andares',
  LISTA: 'Lista da torre',
};

/** O mesmo, por extenso, para o texto de ajuda. */
export const BASIS_HELP: Readonly<Record<AllocationBasis, string>> = {
  DIRETO: 'Quantidade do item multiplicada pelo coeficiente do orçamento.',
  RATEIO_OBRA: 'Parcela desta torre do que o orçamento tem para a obra inteira.',
  RATEIO_ANDARES: 'Dividida em partes iguais pelos andares da torre.',
  LISTA: 'Da lista de material da instalação da torre.',
};

export const KIND_LABEL: Readonly<Record<PurchaseKind, string>> = {
  MATERIAL: 'Material',
  SERVICO_TERCEIRIZADO: 'Terceirizado',
};

export const LINE_STATUS_LABEL: Readonly<Record<ImportRowStatus, string>> = {
  CREATE: 'Criar',
  UPDATE: 'Atualizar',
  UNCHANGED: 'Igual',
  ERROR: 'Erro',
};

/**
 * A quantidade como a linha a guarda.
 *
 * A linha provisória ou de rateio guarda **seis casas** (a parcela de 3 un por 17 andares é
 * 0,176 un), e a tela precisa mostrá-las: formatar pelas casas da unidade escreveria "0 un" e
 * a soma dos pavimentos deixaria de bater com a planilha (RN-345, RN-350). A quantidade de
 * medida já vem arredondada pela unidade, e três casas bastam para mostrá-la inteira.
 */
export function formatPurchaseQuantity(
  quantity: number,
  unit: string,
  provisional: boolean,
): string {
  const text = quantity.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: provisional ? 6 : 3,
  });
  return unit === '' ? text : `${text} ${unit}`;
}

/** `1234.5` → `"1.234,5"` — HH e totais, até duas casas. */
export function formatHours(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// ── Do arquivo às linhas ──────────────────────────────────────────────────────────────

export interface LoadedLines {
  readonly sheetName: string;
  /** As linhas que entram, já com as faces da fachada somadas (RN-352). */
  readonly rows: readonly PurchaseLineRowData[];
  /** Linhas "Não" e "CONFIRMAR": ficam só na planilha, com o motivo. */
  readonly skipped: readonly SkippedLine[];
  /** Linhas que não puderam ser lidas — as outras seguem. */
  readonly issues: readonly RowIssue[];
  /** Quantas linhas da planilha foram somadas a outra da mesma (frente, serviço, material). */
  readonly unions: number;
  /** Quantas linhas de `rows` nasceram de uma soma. */
  readonly unionGroups: number;
  /** O total de cada material como a planilha o escreve — a conferência da RN-350. */
  readonly totals: readonly MaterialTotalData[];
}

export interface LoadedLabor {
  readonly sheetName: string;
  readonly rows: readonly LaborRowData[];
  readonly issues: readonly RowIssue[];
  /** Quantas linhas foram somadas a outra do mesmo (item, função). */
  readonly unions: number;
}

export interface LoadedPurchaseWorkbook {
  readonly fileName: string;
  /** Ausente quando o arquivo não tem a aba "Itens de compra". */
  readonly lines: LoadedLines | undefined;
  /** Ausente quando o arquivo não tem a aba "Mão de obra". */
  readonly labor: LoadedLabor | undefined;
}

const NOT_RECOGNIZED =
  'Não reconheci a planilha de itens de compra. O arquivo precisa ter a aba "Itens de compra" ' +
  'e, se for o caso, a aba "Mão de obra".';

/**
 * Abre o `.xlsx` e lê o que ele tem. O erro é uma frase para a tela, em português.
 *
 * As abas são achadas pelo nome (`detectPurchaseWorkbook`); se o nome foi trocado, vale a que
 * tem o cabeçalho de uma das duas. Um arquivo só com uma das abas é aceito — a tela importa
 * só o que há.
 */
export function loadPurchaseWorkbook(
  bytes: Uint8Array,
  fileName: string,
): Result<LoadedPurchaseWorkbook, string> {
  if (!looksLikeZip(bytes)) {
    return err('Este arquivo não é uma planilha .xlsx. Escolha a planilha de itens de compra.');
  }

  let sheets: readonly XlsxMatrixSheet[];
  try {
    sheets = readXlsxMatrices(bytes);
  } catch {
    return err('Não consegui abrir a planilha: o arquivo parece estar corrompido.');
  }

  const detection = detectPurchaseWorkbook(sheets.map((sheet) => sheet.sheetName));
  const byName = (name: string | null): XlsxMatrixSheet | undefined =>
    name === null ? undefined : sheets.find((sheet) => sheet.sheetName === name);

  const linesSheet =
    byName(detection.linesSheetName) ??
    sheets.find((sheet) => looksLikePurchaseLines(sheet.matrix));
  const laborSheet =
    byName(detection.laborSheetName) ??
    sheets.find((sheet) => sheet !== linesSheet && looksLikeLabor(sheet.matrix));

  return loadMatrices(
    {
      lines:
        linesSheet === undefined
          ? undefined
          : { name: linesSheet.sheetName, matrix: linesSheet.matrix },
      labor:
        laborSheet === undefined
          ? undefined
          : { name: laborSheet.sheetName, matrix: laborSheet.matrix },
    },
    fileName,
  );
}

/** As matrizes já abertas até o arquivo lido — separado para o teste não precisar de bytes. */
export function loadMatrices(
  sheets: {
    readonly lines?: { readonly name: string; readonly matrix: Matrix } | undefined;
    readonly labor?: { readonly name: string; readonly matrix: Matrix } | undefined;
  },
  fileName: string,
): Result<LoadedPurchaseWorkbook, string> {
  if (sheets.lines === undefined && sheets.labor === undefined) return err(NOT_RECOGNIZED);

  let lines: LoadedLines | undefined;
  if (sheets.lines !== undefined) {
    const reading = readPurchaseLinesTemplate(sheets.lines.matrix);
    if (!reading.ok) return err(reading.error.message);
    lines = {
      sheetName: sheets.lines.name,
      rows: reading.value.rows,
      skipped: reading.value.skipped,
      issues: reading.value.issues,
      unions: reading.value.unions,
      unionGroups: reading.value.unionGroups,
      totals: reading.value.totals,
    };
  }

  let labor: LoadedLabor | undefined;
  if (sheets.labor !== undefined) {
    const reading = readLaborTemplate(sheets.labor.matrix);
    if (!reading.ok) return err(reading.error.message);
    labor = {
      sheetName: sheets.labor.name,
      rows: reading.value.rows,
      issues: reading.value.issues,
      unions: reading.value.unions,
    };
  }

  return ok({ fileName, lines, labor });
}

/** O que a tela diz que leu, em uma frase. */
export function describeWorkbook(workbook: LoadedPurchaseWorkbook): string {
  const parts: string[] = [];
  if (workbook.lines !== undefined) {
    const total = workbook.lines.rows.length;
    parts.push(
      `aba "${workbook.lines.sheetName}": ${plural(total, 'linha de compra', 'linhas de compra')}`,
    );
  }
  if (workbook.labor !== undefined) {
    const total = workbook.labor.rows.length;
    parts.push(
      `aba "${workbook.labor.sheetName}": ${plural(total, 'linha de HH', 'linhas de HH')}`,
    );
  }
  return parts.join(' · ');
}

export function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString('pt-BR')} ${count === 1 ? one : many}`;
}

// ── As linhas ignoradas ────────────────────────────────────────────────────────────

export interface SkippedSummary {
  /** `vai_para_sistema` = Não: equipamento, despesa indireta, seguro, risco. */
  readonly notForSystem: number;
  /** `vai_para_sistema` = CONFIRMAR: o bloco repetido da telefonia (D8). */
  readonly toConfirm: number;
}

export function summarizeSkipped(skipped: readonly SkippedLine[]): SkippedSummary {
  let notForSystem = 0;
  let toConfirm = 0;
  for (const line of skipped) {
    if (line.cause === 'TO_CONFIRM') toConfirm += 1;
    else notForSystem += 1;
  }
  return { notForSystem, toConfirm };
}

// ── A prévia do servidor, linha a linha ─────────────────────────────────────────────

/** Uma linha enviada, junto da resposta que o servidor deu a ela. */
export interface JoinedLine<TRow> {
  readonly index: number;
  readonly row: TRow;
  readonly status: ImportRowStatus;
  readonly issues: ImportRowPreview['issues'];
}

/**
 * Junta cada linha enviada à resposta da prévia.
 *
 * O servidor devolve uma resposta por linha, **na ordem em que recebeu** — e é por posição
 * que se junta, porque o número de linha do arquivo pode repetir (as faces somadas ficam com
 * o da primeira). `undefined` quando os tamanhos divergem (uma resposta velha para linhas
 * novas): a tela espera a resposta certa em vez de mostrar uma mentira.
 */
export function joinPreview<TRow>(
  rows: readonly TRow[],
  answers: readonly ImportRowPreview[],
): JoinedLine<TRow>[] | undefined {
  if (answers.length !== rows.length) return undefined;
  return rows.map((row, index) => {
    const answer = answers[index]!;
    return { index, row, status: answer.status, issues: answer.issues };
  });
}

export interface LineCounts {
  readonly create: number;
  readonly update: number;
  readonly unchanged: number;
  readonly error: number;
}

export function countLines(lines: readonly { readonly status: ImportRowStatus }[]): LineCounts {
  const counts = { create: 0, update: 0, unchanged: 0, error: 0 };
  for (const line of lines) {
    if (line.status === 'CREATE') counts.create += 1;
    else if (line.status === 'UPDATE') counts.update += 1;
    else if (line.status === 'UNCHANGED') counts.unchanged += 1;
    else counts.error += 1;
  }
  return counts;
}

/** Quantas linhas de HH e quanto HH há de mão de obra própria e de terceiros (D9). */
export function countLaborByKind(
  rows: readonly Pick<LaborRowData, 'kind' | 'hours'>[],
): Record<LaborRowData['kind'], { readonly lines: number; readonly hours: number }> {
  const counts = { PROPRIA: { lines: 0, hours: 0 }, TERCEIROS: { lines: 0, hours: 0 } };
  for (const row of rows) {
    counts[row.kind].lines += 1;
    counts[row.kind].hours += row.hours;
  }
  return counts;
}
